/**
 * BufferBarrier — "wait for the slowest buffer". If either peer stalls, both
 * auto-pause on the same frame; when both are healthy again the REFERENCE peer
 * schedules a synchronized countdown resume from the slowest position so both
 * restart on the same frame at the same shared instant.
 *
 * Critical distinction: an auto-pause for buffering is NOT a user control event.
 * It must not bump epoch or emit a "user paused" — it is a local, reversible
 * hold. The barrier therefore lives beside the control state, not inside it.
 *
 * Pure state machine: feed it observations + shared time, it returns actions.
 */
import { TIMING } from "../shared/protocol.ts";

export type BarrierPhase = "idle" | "holding" | "arming" | "scheduled";

export interface BarrierObservation {
  selfBuffering: boolean;
  peerBuffering: boolean;
  selfPositionS: number;
  peerPositionS: number;
  isReference: boolean;
  nowSharedMs: number;
}

export type BarrierAction =
  | { type: "none" }
  /** Local auto-pause; remember agreed stall position (slowest). */
  | { type: "hold"; atPositionS: number }
  /** Reference only: broadcast this resume_at to the peer. */
  | { type: "broadcast-resume"; positionS: number; atSharedMs: number }
  /** Both: at atSharedMs, seek to positionS then play. */
  | { type: "schedule-resume"; positionS: number; atSharedMs: number }
  /** Escalation: still buffering past the watchdog. */
  | { type: "watchdog" };

export interface BarrierResumePlan {
  positionS: number;
  atSharedMs: number;
}

export class BufferBarrier {
  private phase: BarrierPhase = "idle";
  private holdPositionS = 0;
  private clearSinceMs = 0;
  private holdStartMs = 0;
  private watchdogFired = false;

  get currentPhase(): BarrierPhase {
    return this.phase;
  }

  /** Non-reference clients call this when they receive a resume_at from the reference. */
  onResumeAt(plan: BarrierResumePlan): BarrierAction {
    this.phase = "scheduled";
    return { type: "schedule-resume", positionS: plan.positionS, atSharedMs: plan.atSharedMs };
  }

  /** Called once the scheduled resume has been executed locally. */
  onResumed(): void {
    this.phase = "idle";
    this.watchdogFired = false;
    this.clearSinceMs = 0;
  }

  /** Force the barrier back to idle (a user/remote action or peer departure has
   * superseded the buffer wait). Clears the memorized hold position so the next
   * hold recomputes it fresh (a stale position must never drive a later resume). */
  reset(): void {
    this.phase = "idle";
    this.watchdogFired = false;
    this.clearSinceMs = 0;
    this.holdPositionS = Infinity;
  }

  /** The safe resume floor: the min position among ONLY the clients that are
   * actually buffering (a client that ran out of data at position P has frames
   * up to P). A non-buffering peer has MORE data, so its position must not drag
   * the resume point down — and its reported position may be stale anyway. */
  private bufferingMin(obs: BarrierObservation): number {
    const ps: number[] = [];
    if (obs.selfBuffering) ps.push(obs.selfPositionS);
    if (obs.peerBuffering) ps.push(obs.peerPositionS);
    return ps.length ? Math.min(...ps) : Math.min(obs.selfPositionS, obs.peerPositionS);
  }

  update(obs: BarrierObservation): BarrierAction {
    const anyBuffering = obs.selfBuffering || obs.peerBuffering;

    // Rising edge from idle -> enter hold. A re-stall from "scheduled" is NOT a
    // fresh hold: it must keep the already-agreed (lower) hold position, so it
    // falls through to the scheduled block below instead of recomputing the min
    // from the (possibly advanced) current positions.
    if (anyBuffering && this.phase === "idle") {
      this.phase = "holding";
      this.holdStartMs = obs.nowSharedMs;
      this.watchdogFired = false;
      // Slowest (min) position is the safe resume point — the stalled client
      // definitely has those frames.
      this.holdPositionS = this.bufferingMin(obs);
      return { type: "hold", atPositionS: this.holdPositionS };
    }

    if (this.phase === "holding") {
      if (anyBuffering) {
        this.holdPositionS = Math.min(this.holdPositionS, this.bufferingMin(obs));
        this.clearSinceMs = 0;
        if (!this.watchdogFired && obs.nowSharedMs - this.holdStartMs >= TIMING.BUFFER_WATCHDOG_MS) {
          this.watchdogFired = true;
          return { type: "watchdog" };
        }
        return { type: "none" };
      }
      // Both clear — begin debounce before arming a resume.
      this.phase = "arming";
      this.clearSinceMs = obs.nowSharedMs;
      return { type: "none" };
    }

    if (this.phase === "arming") {
      // Re-stalled during debounce -> back to holding.
      if (anyBuffering) {
        this.phase = "holding";
        this.clearSinceMs = 0;
        this.holdPositionS = Math.min(this.holdPositionS, this.bufferingMin(obs));
        return { type: "none" };
      }
      if (obs.nowSharedMs - this.clearSinceMs >= TIMING.BUFFER_CLEAR_DEBOUNCE_MS) {
        // Reference schedules the synchronized resume ONCE and tells the peer.
        // The caller treats "broadcast-resume" as: send resume_at to peer AND
        // schedule my own local resume at the same instant.
        if (obs.isReference) {
          const atSharedMs = obs.nowSharedMs + TIMING.RESUME_DELAY_MS;
          this.phase = "scheduled";
          return { type: "broadcast-resume", positionS: this.holdPositionS, atSharedMs };
        }
        // Non-reference normally waits for the reference's resume_at. Failsafe:
        // if it never arrives (lost message, reference recovered its own stall
        // without arming, etc.), self-resume so we can never get stuck paused.
        if (obs.nowSharedMs - this.clearSinceMs >= TIMING.BARRIER_FAILSAFE_MS) {
          this.phase = "scheduled";
          return { type: "schedule-resume", positionS: this.holdPositionS, atSharedMs: obs.nowSharedMs + TIMING.RESUME_DELAY_MS };
        }
        return { type: "none" };
      }
      return { type: "none" };
    }

    if (this.phase === "scheduled") {
      // Re-stalled before the scheduled instant -> cancel and re-hold.
      if (anyBuffering) {
        this.phase = "holding";
        this.clearSinceMs = 0;
        this.holdPositionS = Math.min(this.holdPositionS, this.bufferingMin(obs));
        return { type: "hold", atPositionS: this.holdPositionS };
      }
      // Already scheduled (once). Wait for the resume instant to arrive; the
      // caller fires the actual seek+play from its scheduled timer.
      return { type: "none" };
    }

    return { type: "none" };
  }

  get holdPosition(): number {
    return this.holdPositionS;
  }
}
